/**
 * nexchat.js — NexChat Application Controller
 *
 * Owns application state and orchestrates auth.js, matrix.js, and ui.js.
 * This is the single source of truth for app state.
 *
 * State model:
 *   UNAUTHENTICATED → AUTH_CALLBACK → LOADING → READY → (room selected)
 *
 * Nothing in matrix.js or ui.js holds state. All state is here.
 */

import {
  configureAuth,
  setTokenRefreshCallback,
  initiateLogin,
  handleCallback,
  restoreSession,
  getStoredTokens,
  saveUserId,
  logout as authLogout,
  scheduleTokenRefresh,
  refreshAccessToken,
  getDeviceId,
} from './auth.js';

import { initI18n, setLocale, getLocale, getLocaleNames, t } from './i18n.js';
import { initNexAI, isNexAIReady, nexaiChat, nexaiSummarise, nexaiDraft, clearNexAIToken, getNexAIToken } from './nexai.js';

import {
  initMatrix,
  stopMatrix,
  updateAccessToken,
  getRooms,
  getRoom,
  sendMessage,
  sendTypingNotification,
  markRoomAsRead,
  createRoom,
  buildUserId,
  getMyUserId,
  getMyDisplayName,
  getHomeserverDomain,
  fetchUserProfile,
  isDmRoom,
  getDmPartner,
  getDisplayName,
  getRoomReadReceipts,
  getReadByList,
  setRoomTopic,
  getRoomTopic,
  kickUser,
  sendPost,
  getOrCreateStoriesRoom,
  postStory,
  getActiveStories,
  STORY_UPDATED,
  debugReceipts,
  getTimeline,
  formatMessage,
  sendReaction,
  editMessage,
  uploadFile,
  sendFile,
  sendExternalMedia,
  decryptAndBlobUrl,
  mxcToHttp,
  updateDisplayName,
  updateAvatar,
  getMyProfile,
  pinMessage,
  unpinMessage,
  getPinnedEventIds,
  loadMoreHistory,
  backfillDmRoomNames,
  sendVoiceMessage,
  sendPoll,
  sendPollVote,
  endPoll,
  getPollResults,
  sendThreadReply,
  getThreadReplies,
  getThreadSummary,
  getElementCallUrl,
  sendCallInvite,
  sendCallEnd,
  isCallInviteEvent,
  setDisappearingMessages,
  getDisappearingMessageTtl,
  purgeExpiredMessages,
  emptyRoom,
  clearMyMessages,
  leaveRoom,
  isRoomAdmin,
  getUserAvatarUrl,
  getRoomAvatarUrl,
  prewarmAvatarCache,
  setPresence,
  getUserPresenceStatus,
  getRoomNotifSetting,
  setRoomNotifSetting,
  setEmojiStatus,
  getEmojiStatusForUser,
  searchMessages,
  searchMessagesServer,
  redactEvent,
  getReactionsForEvent,
  EV,
} from './matrix.js';

import {
  renderAuthScreen,
  renderLoadingScreen,
  renderAppShell,
  renderRoomList,
  renderChatPanel,
  renderTimeline,
  appendMessage,
  updateMessageInPlace,
  updateMessageTick,
  updateReceiptAvatars,
  updateRoomTopic,
  showTopicEditModal,
  showFileSendPreview,
  showBookmarkFolderPicker,
  showBookmarksPanel,
  attachLinkPreview,
  flushPdfRenders,
  showWorkingHoursModal,
  getWorkingHours,
  isInWorkingHours,
  showPostComposer,
  renderStoriesBar,
  showStoryComposer,
  showStoryViewer,
  wireReplyButtons,
  setReplyingTo,
  appendNexAIMessage,
  showNexAITyping,
  hideNexAITyping,
  setNexAIButtonState,
  updateNexAIMessage,
  updateTypingIndicator,
  setConnectionStatus,
  renderNewRoomModal,
  setActiveRoom,
  updateRoomUnread,
  showToast,
  updateMessageReactions,
  showEditInput,
  showForwardModal,
  setFileUploadProgress,
  showProfileModal,
  showNotifSettings,
  showPinsPanel,
  closePinsPanel,
  showGifStickerPicker,
  showSearchPanel,
  closeSearchPanel,
  closeNexAIPanel,
  toggleNexAIPanel,
  showReadByPopup,
  initVoiceRecorder,
  buildVoicePlayer,
  showStarredPanel,
  closeStarredPanel,
  showPollCreator,
  buildPollCard,
  openThreadPanel,
  closeThreadPanel,
  updateThreadPanel,
  openCallModal,
  closeCallModal,
  showIncomingCallBanner,
  showCallPip,
  applyTheme,
  loadSavedTheme,
  showThemePicker,
  showLanguagePicker,
  showEmojiStatusModal,
  showRoomNotifSettings,
  loadEmojiStatus,
  _updateEmojiStatusDisplay,
  showScheduleModal,
  showScheduledList,
  showSlashMenu,
  removeSlashMenu,
  navigateSlashMenu,
  getActiveSlashCommand,
  showMentionMenu,
  removeMentionMenu,
  navigateMentionMenu,
  getActiveMention,
} from './ui.js';

async function refreshIfNeeded() {
  const { expiresAt, refreshToken } = getStoredTokens();
  if (!expiresAt || !refreshToken) return;
  if (Date.now() > expiresAt - 30000) {
    await refreshAccessToken();
  }
}

/* ──────────────────────────────────────────────
   Configuration
   Update these values for your deployment.
─────────────────────────────────────────────── */
const CONFIG = {
  homeserverUrl: 'https://nexchat.ethosserver.com',
  masUrl:        'https://auth.ethosserver.com',
  clientId:      '01KJ1MJEP0TH2VQ7KJB7ZDPC89',
  redirectUri:   window.location.origin + '/',           // must match MAS registration
};

/* ──────────────────────────────────────────────
   Application State
─────────────────────────────────────────────── */
const state = {
  phase:          'BOOT',
  activeRoomId:   null,
  syncReady:      false,
  replyingTo:     null,
};

/* ──────────────────────────────────────────────
   Bootstrap
─────────────────────────────────────────────── */
export async function boot() {
  // Apply saved theme immediately on boot
  const _savedTheme = localStorage.getItem('nx_theme') || 'dark';
  document.body.setAttribute('data-theme', _savedTheme);

  // Init i18n
  initI18n();

  // 1. Configure auth module
  configureAuth({
    homeserverUrl: 'https://nexchat.ethosserver.com',
    masUrl:        'https://auth.ethosserver.com',
    clientId:      '01KJ1MJEP0TH2VQ7KJB7ZDPC89',
    redirectUri:   CONFIG.redirectUri,
  });

  // 2. When token is refreshed, update the Matrix client
  setTokenRefreshCallback((newToken) => {
    updateAccessToken(newToken);
  });

  // 3. Handle session expiry
  window.addEventListener('nx:session-expired', () => {
    showToast('Your session expired. Please sign in again.', 'error');
    setTimeout(() => gotoAuth(), 1500);
  });

  // 4. Check if this is an OIDC callback
  if (window.location.search.includes('code=')) {
    renderLoadingScreen('Completing sign in…');
    try {
      await handleCallback();
    } catch (err) {
      console.error('[boot] Callback error:', err);
      renderAuthScreen(handleLoginClick);
      return;
    }
  }

  // 5. Try to restore existing session
  renderLoadingScreen('Restoring session…');

  let accessToken = null;
  try {
    accessToken = await restoreSession();
  } catch (err) {
    console.error('[boot] Session restore error:', err);
  }

  if (!accessToken) {
    gotoAuth();
    return;
  }

  // 6. Ensure we have a userId (needed to init Matrix client)
  let { userId, deviceId } = getStoredTokens();
  if (!userId) {
    try {
      const profile = await fetchUserProfile(accessToken, CONFIG.homeserverUrl);
      userId = profile.user_id;
      saveUserId(userId);
    } catch (err) {
      console.error('[boot] whoami failed:', err);
      gotoAuth();
      return;
    }
  }

  await startMatrixSession(accessToken, userId, deviceId || getDeviceId());
}

/* ──────────────────────────────────────────────
   Auth flow
─────────────────────────────────────────────── */
function gotoAuth() {
  state.phase = 'AUTH';
  renderAuthScreen(handleLoginClick);
}

async function handleLoginClick() {
  await initiateLogin(); // redirects to MAS — no return from this
}

/* ──────────────────────────────────────────────
   Matrix session start
─────────────────────────────────────────────── */
async function startMatrixSession(accessToken, userId, deviceId) {
  state.phase = 'LOADING';
  renderLoadingScreen('Starting NexChat…');

  try {
    await initMatrix({
      homeserverUrl: 'https://nexchat.ethosserver.com',
      accessToken,
      userId,
      deviceId,
    });
  } catch (err) {
    console.error('[boot] Matrix init failed:', err);
    showToast('Failed to connect to server.', 'error');
    gotoAuth();
    return;
  }

  // Bind Matrix events before sync
  bindMatrixEvents();

  // Wait for sync PREPARED
  // (already handled in bindMatrixEvents via EV.READY)
}

/* ──────────────────────────────────────────────
   Matrix event handlers
─────────────────────────────────────────────── */
function bindMatrixEvents() {
  window.addEventListener(EV.READY, handleMatrixReady, { once: true });
  window.addEventListener(EV.SYNC_STATE,    handleSyncState);
  window.addEventListener(EV.ROOM_UPDATE,   handleRoomUpdate);
  window.addEventListener(EV.ROOM_TIMELINE, handleTimeline);
  window.addEventListener(EV.TYPING,        handleTyping);
  window.addEventListener(EV.READ_RECEIPT,  handleReadReceipt);
  window.addEventListener(EV.REACTION,      handleReaction);

  window.addEventListener('nx:preview-sound', ({ detail: { type } }) => {
    playTone(type);
  });
  window.addEventListener('nx:set-presence', async ({ detail: { status } }) => {
    try {
      await setPresence(status);
    } catch (_) {}
  });
  window.addEventListener(EV.REDACTION,     handleRedaction);
  _initWorkingHoursScheduler();
  _initStories();
  window.addEventListener(EV.ROOM_TOPIC, ({ detail: { roomId, topic } }) => {
    if (roomId === state.activeRoomId) updateRoomTopic(topic);
  });
  window.addEventListener(EV.PINS_UPDATED,  ({ detail: { roomId } }) => {
    if (roomId === state.activeRoomId) {
      const room = getRoom(roomId);
      updatePinsBadge(getPinnedEventIds(room).length);
    }
  });
}

function handleMatrixReady() {
  state.phase     = 'READY';
  state.syncReady = true;

  const userId  = getMyUserId();
  const name    = getMyDisplayName();

  renderAppShell(
    name,
    userId,
    openNewRoomModal,
    handleLogout,
    null,
    async () => {
      const profile = await getMyProfile();
      const avatarUrl = profile.avatarUrl ? mxcToHttp(profile.avatarUrl) : null;
      showProfileModal(state.displayName, avatarUrl, async (newName, avatarFile) => {
        try {
          if (avatarFile) {
            const { content_uri } = await uploadFile(avatarFile);
            await updateAvatar(content_uri);
          }
          if (newName && newName !== state.displayName) {
            await updateDisplayName(newName);
            state.displayName = newName;
            const nameEl = document.getElementById('user-display-name');
            if (nameEl) nameEl.textContent = newName;
            const avatarEl = document.getElementById('user-avatar-el');
            if (avatarEl) {
              avatarEl.style.background = '';
              avatarEl.childNodes[0].textContent = newName.slice(0,2).toUpperCase();
            }
          }
          showToast(t('toast.profile_updated'), 'success');
        } catch (err) {
          showToast('Update failed: ' + err.message, 'error');
        }
      });
    },
    // onShowScheduled
    async () => {
      const jwt = await getNexAIJwt();
      showScheduledList(
        () => getNexAIJwt(),
        (roomId) => getRoom(roomId)?.name || roomId
      );
    },
  );

  renderRoomList(getRooms(), state.activeRoomId, selectRoom);
  prewarmAvatarCache(); // pre-load all avatars from SDK cache

  // Init push notifications (silent — only subscribes if already granted)
  initPushNotifications().catch(() => {});

  // Init offline message sync
  _initOfflineSync();

  // Handle emoji status save
  window.addEventListener('nx:set-emoji-status', async (e) => {
    const { status } = e.detail || {};
    if (status?.emoji || status?.text) {
      const statusText = `${status.emoji || ''} ${status.text || ''}`.trim();
      await setPresence('online', statusText).catch(() => {});
      await setEmojiStatus(status.emoji, status.text).catch(() => {});
    } else {
      await setPresence('online', '').catch(() => {});
      await setEmojiStatus('', '').catch(() => {});
    }
  });

  // Clear favicon badge when window focused
  window.addEventListener('focus', () => {
    if (state.activeRoomId) setTimeout(_updateTotalUnreadBadge, 300);
  });

  // Listen for emoji status updates (room state events)
  window.addEventListener(EV.PRESENCE, (e) => {
    const { userId, statusMsg, emoji } = e.detail || {};
    if (!userId || !state.activeRoomId) return;
    const room = getRoom(state.activeRoomId);
    if (!room) return;
    const members = room.getJoinedMembers().filter(m => m.userId !== getMyUserId());
    if (members.some(m => m.userId === userId)) {
      const el = document.getElementById('partner-status-msg');
      if (el) {
        const text = emoji || statusMsg
          ? `${emoji || ''} ${statusMsg || ''}`.trim()
          : '';
        el.textContent = text;
      }
    }
  });

  // Load saved emoji status
  loadEmojiStatus();
  window._flushOfflineQueue = flushOfflineQueue;
  window._getOfflineQueue = getOfflineQueue;

  // Mobile sidebar toggle
  _initMobileSidebar();
  // Expose poll results getter for poll card renderer
  window._getPollResults = getPollResults;
  window._debugReceipts = debugReceipts;
  window._getRoom = getRoom;
  window._getRoom = getRoom;
  window._sendPollVote = async (rId, pollId, answerIds) => {
    try { await sendPollVote(rId, pollId, answerIds); }
    catch (err) { showToast('Vote failed: ' + err.message, 'error'); }
  };
  window._endPoll = async (rId, pollId) => {
    try { await endPoll(rId, pollId); showToast('Poll ended', 'success'); }
    catch (err) { showToast('Failed to end poll: ' + err.message, 'error'); }
  };

  // Backfill names for existing unnamed DM rooms (runs once, silent)
  backfillDmRoomNames().catch(() => {});

  // Restore last active room from storage
  const lastRoom = localStorage.getItem('nx_last_room');
  if (lastRoom && getRoom(lastRoom)) {
    selectRoom(lastRoom);
  }

  scheduleTokenRefresh();

  // Pre-init audio context on app load (may be suspended until user gesture)
  initAudio();

  // Disappearing messages — check every 60s, only rooms with explicit TTL set
  // Never run on boot — wait 2 minutes first to avoid wiping on reload
  if (window._disappearTimer) clearInterval(window._disappearTimer);
  const _disappearBootTime = Date.now();
  window._disappearTimer = setInterval(async () => {
    // Grace period: don't purge within 2 minutes of boot
    if (Date.now() - _disappearBootTime < 120000) return;
    const rooms = getRooms();
    for (const room of rooms) {
      // Only run if m.room.retention exists AND has a positive max_lifetime
      const retEv = room.currentState.getStateEvents('m.room.retention', '');
      if (!retEv) continue; // never set — skip
      const ttl = retEv.getContent()?.max_lifetime;
      if (!ttl || ttl <= 0) continue; // explicitly off — skip
      await purgeExpiredMessages(room.roomId).catch(() => {});
    }
  }, 60000);

  // Init NexAI — exchange Matrix token for NexChat JWT
  const { accessToken } = getStoredTokens();
  if (accessToken) {
    initNexAI(accessToken).then(ok => {
      if (ok) console.info('[nexai] Ready');
      else console.warn('[nexai] Init failed — NexAI unavailable');
    });
  }
}

function handleSyncState({ detail: { state: syncState } }) {
  const connected = syncState === 'PREPARED' || syncState === 'SYNCING';
  setConnectionStatus(connected);
}

function handleRoomUpdate() {
  if (state.phase !== 'READY') return;
  renderRoomList(getRooms(), state.activeRoomId, selectRoom);
  prewarmAvatarCache(); // pre-load all avatars from SDK cache

  // Init push notifications (silent — only subscribes if already granted)
  initPushNotifications().catch(() => {});

  // Init offline message sync
  _initOfflineSync();

  // Handle emoji status save
  window.addEventListener('nx:set-emoji-status', async (e) => {
    const { status } = e.detail || {};
    if (status?.emoji || status?.text) {
      const statusText = `${status.emoji || ''} ${status.text || ''}`.trim();
      await setPresence('online', statusText).catch(() => {});
      await setEmojiStatus(status.emoji, status.text).catch(() => {});
    } else {
      await setPresence('online', '').catch(() => {});
      await setEmojiStatus('', '').catch(() => {});
    }
  });

  // Clear favicon badge when window focused
  window.addEventListener('focus', () => {
    if (state.activeRoomId) setTimeout(_updateTotalUnreadBadge, 300);
  });

  // Listen for emoji status updates (room state events)
  window.addEventListener(EV.PRESENCE, (e) => {
    const { userId, statusMsg, emoji } = e.detail || {};
    if (!userId || !state.activeRoomId) return;
    const room = getRoom(state.activeRoomId);
    if (!room) return;
    const members = room.getJoinedMembers().filter(m => m.userId !== getMyUserId());
    if (members.some(m => m.userId === userId)) {
      const el = document.getElementById('partner-status-msg');
      if (el) {
        const text = emoji || statusMsg
          ? `${emoji || ''} ${statusMsg || ''}`.trim()
          : '';
        el.textContent = text;
      }
    }
  });

  // Load saved emoji status
  loadEmojiStatus();
  window._flushOfflineQueue = flushOfflineQueue;
  window._getOfflineQueue = getOfflineQueue;

  // Mobile sidebar toggle
  _initMobileSidebar();
  // Expose poll results getter for poll card renderer
  window._getPollResults = getPollResults;
  window._debugReceipts = debugReceipts;
  window._getRoom = getRoom;
  window._getRoom = getRoom;
  window._sendPollVote = async (rId, pollId, answerIds) => {
    try { await sendPollVote(rId, pollId, answerIds); }
    catch (err) { showToast('Vote failed: ' + err.message, 'error'); }
  };
  window._endPoll = async (rId, pollId) => {
    try { await endPoll(rId, pollId); showToast('Poll ended', 'success'); }
    catch (err) { showToast('Failed to end poll: ' + err.message, 'error'); }
  };
}

function _attachPreviewForEvent(event) {
  const token = localStorage.getItem('nx_nexai_token');
  if (!token) return;
  const content = event.getContent?.();
  if (!content || content.msgtype !== 'm.text') return;
  const body = content.body || '';
  if (!body.includes('http')) return;
  const eventId = event.getId();
  const row = document.querySelector('.message-row[data-event-id="' + eventId + '"]');
  const wrapper = row?.firstElementChild || row;
  if (wrapper) attachLinkPreview(wrapper, body, token);
}

function handleTimeline({ detail: { roomId, event, encrypted, decryptedUpdate } }) {
  if (state.phase !== 'READY') return;

  if (roomId === state.activeRoomId) {
    if (decryptedUpdate) {
      // Event is now decrypted — check if it's a thread reply
      const rel = event.getContent?.()?.['m.relates_to'];
      const isThreadReply = rel?.rel_type === 'm.thread';
      if (isThreadReply) {
        // Remove the encrypted placeholder from main timeline
        const container = document.getElementById('messages-container');
        const eventId = event.getId();
        const txnId = event.getUnsigned()?.transaction_id;
        if (container) {
          const el = container.querySelector(`[data-event-id="${eventId}"]`)
                  || (txnId ? container.querySelector(`[data-txn-id="${txnId}"]`) : null);
          if (el) {
            const row = el.closest('.message-row') || el;
            row.remove();
          }
        }
        updateThreadPanel(getRoom(roomId), rel.event_id);
        setTimeout(() => _updateThreadBadges(roomId), 200);
      } else {
        updateMessageInPlace(event);
        markRoomAsRead(getRoom(roomId));
        setTimeout(() => _attachPreviewForEvent(event), 300);
      }
      // Schedule a timeline re-render to fill any gaps left by removed thread placeholders
      if (!window._threadReRenderTimer) {
        window._threadReRenderTimer = setTimeout(() => {
          window._threadReRenderTimer = null;
          const room = getRoom(roomId);
          if (room && roomId === state.activeRoomId) {
            renderTimeline(room);
            setTimeout(() => _updateThreadBadges(roomId), 100);
            requestAnimationFrame(() => {
              flushPdfRenders();
              const token = localStorage.getItem('nx_nexai_token');
              if (token) {
                room.getLiveTimeline().getEvents().forEach(ev => {
                  const content = ev.getContent?.();
                  if (content?.msgtype !== 'm.text') return;
                  const body = content.body || '';
                  if (!body.match(/https?:\/\/[^\s]{10,}/)) return;
                  const row = document.querySelector('.message-row[data-event-id="' + ev.getId() + '"]');
                  const wrapper = row?.firstElementChild || row;
                  if (wrapper) attachLinkPreview(wrapper, body, token);
                });
              }
            });
          }
        }, 800);
      }
    } else if (encrypted) {
      // Encrypted placeholder — always show in main timeline until decrypted
      appendMessage(event, getRoom(roomId));
    } else {
      // Plain unencrypted message — check for thread reply
      const rel = event.getContent?.()?.['m.relates_to'];
      const isThreadReply = rel?.rel_type === 'm.thread';
      if (isThreadReply) {
        updateThreadPanel(getRoom(roomId), rel.event_id);
        setTimeout(() => _updateThreadBadges(roomId), 200);
      } else {
        appendMessage(event, getRoom(roomId));
        markRoomAsRead(getRoom(roomId));
        setTimeout(() => _updateRoomTicks(getRoom(roomId)), 500);
        setTimeout(() => _attachPreviewForEvent(event), 300);
      }
    }
  }

  // Trigger notification — for E2EE rooms events come as decryptedUpdate:true
  // Only notify for recent messages (within last 10 seconds) to avoid notifying on load
  if (event.getType() === 'm.room.message') {
    const age = Date.now() - event.getTs();
    if (age < 10000) {
      handleIncomingMessage(roomId, event);
      // Trigger push notifications for room members
      if (event.getSender() !== getMyUserId()) {
        triggerPushForMessage(roomId, event);
      }
      // Check for incoming call invite from another user
      if (isCallInviteEvent(event) && event.getSender() !== getMyUserId()) {
        _handleIncomingCall(roomId, event);
      }
    }
  }

  updateRoomUnread(roomId);
}

let _typingClearTimer = null;
function handleTyping({ detail: { roomId, users } }) {
  if (roomId !== state.activeRoomId) return;
  const others = users.filter(u => u !== getMyUserId());
  if (others.length > 0) {
    // Users are typing — show immediately, clear any pending hide
    clearTimeout(_typingClearTimer);
    updateTypingIndicator(roomId, users);
  } else {
    // Users stopped — delay clearing by 2s to avoid flicker
    clearTimeout(_typingClearTimer);
    _typingClearTimer = setTimeout(() => {
      updateTypingIndicator(roomId, []);
    }, 2000);
  }
}

function handleReadReceipt({ detail: { roomId } }) {
  if (!roomId) return;
  updateRoomUnread(roomId);
  if (roomId === state.activeRoomId) {
    // Delay slightly so SDK has time to store the receipt before we query it
    setTimeout(() => _updateRoomTicks(getRoom(roomId)), 300);
  }
}

/* ──────────────────────────────────────────────
   Room selection
─────────────────────────────────────────────── */
function selectRoom(roomId) {
  if (state.activeRoomId === roomId) return;

  state.activeRoomId = roomId;
  localStorage.setItem('nx_last_room', roomId);
  // Update favicon badge (room read = fewer unread)
  setTimeout(_updateTotalUnreadBadge, 500);
  // Close mobile sidebar when room selected
  if (window.innerWidth <= 768) {
    document.querySelector('.sidebar')?.classList.remove('mobile-open');
    document.getElementById('sidebar-overlay')?.classList.remove('visible');
  }

  const room = getRoom(roomId);
  if (!room) return;

  setActiveRoom(roomId);
  window._currentRoomId = roomId;

  state.replyingTo = null;
  setReplyingTo(null);

  renderChatPanel(
    room,
    (text) => handleSendMessage(roomId, text),
    (isTyping) => sendTypingNotification(roomId, isTyping),
    (text, callbacks) => handleNexAIRequest(roomId, text, callbacks),
    () => {
      showSearchPanel(roomId, async (query) => {
        try {
          const clientResults = searchMessages(query, roomId);
          const matrixToken = localStorage.getItem('nx_access_token') || '';
          const serverResults = await searchMessagesServer(query, roomId, matrixToken).catch(() => []);
          const seen = new Set(serverResults.map(r => r.eventId));
          const merged = [
            ...serverResults,
            ...clientResults.filter(r => !seen.has(r.eventId))
          ];
          return merged.slice(0, 100);
        } catch (err) {
          showToast('Search failed: ' + err.message, 'error');
          return [];
        }
      }, (eventId) => {
        const el = document.querySelector('.message-row[data-event-id="' + eventId + '"]');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('highlight');
          setTimeout(() => el.classList.remove('highlight'), 2000);
        }
      });
    },
  );

  renderTimeline(room);
  markRoomAsRead(room);

  // Flush deferred PDF renders and link previews after DOM is ready
  requestAnimationFrame(() => {
    flushPdfRenders();
    const token = localStorage.getItem('nx_nexai_token');
    if (token) {
      const timeline = room.getLiveTimeline().getEvents();
      for (const event of timeline) {
        const content = event.getContent?.();
        if (content?.msgtype !== 'm.text') continue;
        const body = content.body || '';
        if (!body.match(/https?:\/\/[^\s]{10,}/)) continue;
        const eventId = event.getId();
        const row = document.querySelector('.message-row[data-event-id="' + eventId + '"]');
        const wrapper = row?.firstElementChild || row;
        if (wrapper) attachLinkPreview(wrapper, body, token);
      }
    }
  });

  // Load and wire room topic — element only exists for non-DM rooms
  if (!isDmRoom(room)) {
    const _topic = getRoomTopic(room);
    updateRoomTopic(_topic);
    document.getElementById('chat-topic-edit')?.addEventListener('click', () => {
      const current = getRoomTopic(getRoom(roomId));
      showTopicEditModal(current, async (newTopic) => {
        try {
          await setRoomTopic(roomId, newTopic);
          updateRoomTopic(newTopic);
          showToast(newTopic ? 'Topic updated' : 'Topic cleared', 'success');
        } catch (err) {
          showToast('Failed to update topic: ' + err.message, 'error');
        }
      });
    });
  }



  // Render reactions immediately after timeline render
  requestAnimationFrame(() => {
    const _container = document.getElementById('messages-container');
    if (_container) {
      _container.querySelectorAll('.message-row[data-event-id]').forEach(el => {
        const eventId = el.dataset.eventId;
        if (!eventId || eventId.startsWith('~')) return;
        const reactions = getReactionsForEvent(room, eventId);
        if (Object.keys(reactions).length === 0) return;
        updateMessageReactions(eventId, reactions, getMyUserId(), (emoji, myReactionEventId) => {
          if (myReactionEventId) {
            redactEvent(roomId, myReactionEventId).catch(console.error);
          } else {
            sendReaction(roomId, eventId, emoji).catch(console.error);
          }
        });
      });
    }
  });

  // Expose room members for @mention autocomplete
  window._getAllRooms = () => getRooms().map(r => ({ roomId: r.roomId, name: r.name || r.roomId }));
  window._getRoomMembers = () => {
    const r = getRoom(roomId);
    if (!r) return [];
    const myId = getMyUserId();
    return r.getJoinedMembers().filter(m => m.userId !== myId);
  };

  // Load partner emoji status for DMs
  const _members = room.getJoinedMembers().filter(m => m.userId !== getMyUserId());
  if (_members.length === 1) {
    const _partner = _members[0];
    const _updateStatus = () => {
      getEmojiStatusForUser(_partner.userId, roomId).then(status => {
        const el = document.getElementById('partner-status-msg');
        if (el) el.textContent = (status?.text || status?.emoji)
          ? `${status.emoji || ''} ${status.text || ''}`.trim() : '';
      }).catch(() => {});
    };
    _updateStatus();
    setTimeout(_updateStatus, 1500);
    setTimeout(_updateStatus, 4000);
  }

  // Wire pins button
  document.getElementById('pins-btn')?.addEventListener('click', () => {
    const room = getRoom(roomId);
    const pinIds = getPinnedEventIds(room);
    const pinnedMessages = room.getLiveTimeline().getEvents()
      .filter(e => pinIds.includes(e.getId()))
      .map(e => ({
        id: e.getId(),
        body: e.getContent()?.body || '',
        senderId: e.getSender(),
        ts: e.getTs(),
      }));
    showPinsPanel(pinnedMessages, (eventId) => {
      // Jump to message
      const el = document.querySelector('.message-row[data-event-id="' + eventId + '"]');
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.classList.add('highlight'); setTimeout(() => el.classList.remove('highlight'), 2000); }
      closePinsPanel();
    }, (eventId) => {
      unpinMessage(roomId, eventId).then(() => showToast('Unpinned', 'success')).catch(() => {});
    });
  });

  // Wire starred messages button
  document.getElementById('working-hours-btn')?.addEventListener('click', () => {
    document.getElementById('profile-dropdown')?.classList.remove('open');
    showWorkingHoursModal();
  });

  document.getElementById('bookmarks-btn')?.addEventListener('click', () => {
    if (document.getElementById('bookmarks-panel')) { document.getElementById('bookmarks-panel').remove(); return; }
    showBookmarksPanel((eventId, targetRoomId) => {
      const jumpToBookmark = () => {
        const el = document.querySelector('[data-event-id="' + eventId + '"]');
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el?.classList.add('highlight-flash');
        setTimeout(() => el?.classList.remove('highlight-flash'), 1500);
      };
      if (targetRoomId && targetRoomId !== state.activeRoomId) {
        selectRoom(targetRoomId);
        setTimeout(jumpToBookmark, 800);
      } else {
        jumpToBookmark();
      }
    });
  });
  document.getElementById('starred-btn')?.addEventListener('click', () => {
    if (document.getElementById('starred-panel')) { closeStarredPanel(); return; }
    showStarredPanel(roomId, (eventId, targetRoomId) => {
      const jumpToMsg = () => {
        setTimeout(() => {
          const el = document.querySelector('.message-row[data-event-id="' + eventId + '"]');
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('highlight');
            setTimeout(() => el.classList.remove('highlight'), 2000);
          }
        }, 600);
      };
      if (targetRoomId && targetRoomId !== state.activeRoomId) {
        selectRoom(targetRoomId);
        jumpToMsg();
      } else {
        jumpToMsg();
      }
    });
  });

  // Load older messages handler
  const msgContainer = document.getElementById('messages-container');
  let _loadingHistory = false;

  const doLoadMore = async () => {
    if (_loadingHistory) return;
    _loadingHistory = true;
    const btn = document.getElementById('load-more-btn');
    if (btn) btn.textContent = 'Loading…';
    const prevHeight = msgContainer?.scrollHeight || 0;
    try {
      const room = getRoom(roomId);
      await loadMoreHistory(room);
      await loadMoreHistory(room);
      renderTimeline(room);
      _updateRoomTicks(getRoom(roomId));
      requestAnimationFrame(() => {
        if (msgContainer) {
          const diff = msgContainer.scrollHeight - prevHeight;
          msgContainer.scrollTop = diff > 0 ? diff : 0;
        }
      });
    } catch (_) {}
    setTimeout(() => { _loadingHistory = false; }, 800);
  };

  if (msgContainer) {
    msgContainer.addEventListener('scroll', () => {
      const mc = document.getElementById('messages-container');
      if (mc && mc.scrollTop < 50) doLoadMore();
    });
  }

  // Wire load more link click (dispatched from renderTimeline)
  // Remove previous listener before adding new one
  window._nexLoadMore && window.removeEventListener('nx:load-more-history', window._nexLoadMore);
  window._nexLoadMore = doLoadMore;
  window.addEventListener('nx:load-more-history', doLoadMore);
  if (msgContainer) {
    wireReplyButtons(msgContainer, (msg) => {
      state.replyingTo = msg;
      setReplyingTo(msg);
    });

    // Wire message action events
    msgContainer.addEventListener('nx:send-gif', async (e) => {
      const { url } = e.detail;
      try {
        // Fetch and upload GIF to Matrix media
        const res = await fetch(url);
        const blob = await res.blob();
        const file = new File([blob], 'gif.gif', { type: 'image/gif' });
        setFileUploadProgress(true, 'GIF');
        const { content_uri } = await uploadFile(file);
        await sendFile(roomId, file, content_uri);
        setFileUploadProgress(false);
      } catch (err) {
        setFileUploadProgress(false);
        showToast('Failed to send GIF: ' + err.message, 'error');
      }
    });

    msgContainer.addEventListener('nx:send-sticker', async (e) => {
      const { url } = e.detail;
      try {
        // Send sticker as image message
        const res = await fetch(url);
        const blob = await res.blob();
        const file = new File([blob], 'sticker.png', { type: blob.type || 'image/png' });
        setFileUploadProgress(true, 'Sticker');
        const { content_uri } = await uploadFile(file);
        await sendFile(roomId, file, content_uri);
        setFileUploadProgress(false);
      } catch (err) {
        setFileUploadProgress(false);
        showToast('Failed to send sticker: ' + err.message, 'error');
      }
    });

    msgContainer.addEventListener('nx:pin', (e) => {
      const { eventId, isPinned } = e.detail;
      if (isPinned) {
        unpinMessage(roomId, eventId).catch(err => showToast('Unpin failed: ' + err.message, 'error'));
      } else {
        pinMessage(roomId, eventId).then(() => showToast('Message pinned', 'success')).catch(err => showToast('Pin failed: ' + err.message, 'error'));
      }
    });

    msgContainer.addEventListener('nx:delete', (e) => {
      const { eventId } = e.detail;
      redactEvent(roomId, eventId).catch(err => showToast('Delete failed: ' + err.message, 'error'));
    });

    msgContainer.addEventListener('nx:edit', (e) => {
      const { eventId, currentBody } = e.detail;
      showEditInput(eventId, currentBody, (newBody) => {
        editMessage(roomId, eventId, newBody).catch(err => showToast('Edit failed: ' + err.message, 'error'));
      });
    });

    msgContainer.addEventListener('nx:action', (e) => {
      const { action, eventId } = e.detail || {};
      if (action === 'translate') _handleTranslateMessage(eventId, roomId);
    });

    msgContainer.addEventListener('nx:copy', (e) => {
      const text = e.detail.body;
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => showToast('Copied', 'success')).catch(() => {
          const el = document.createElement('textarea');
          el.value = text; document.body.appendChild(el); el.select();
          document.execCommand('copy'); document.body.removeChild(el);
          showToast('Copied', 'success');
        });
      } else {
        const el = document.createElement('textarea');
        el.value = text; document.body.appendChild(el); el.select();
        document.execCommand('copy'); document.body.removeChild(el);
        showToast('Copied', 'success');
      }
    });

    msgContainer.addEventListener('nx:reply-click', (e) => {
      state.replyingTo = e.detail;
      setReplyingTo(e.detail);
    });

    msgContainer.addEventListener('nx:open-thread', (e) => {
      const { eventId } = e.detail;
      const room = getRoom(roomId);
      const event = room?.getLiveTimeline().getEvents().find(ev => ev.getId() === eventId);
      if (!event || !room) return;
      openThreadPanel(event, room, getMyUserId(), async (rootId, text) => {
        try {
          await sendThreadReply(roomId, rootId, text);
        } catch (err) {
          showToast('Failed to send reply: ' + err.message, 'error');
        }
      });
    });

    msgContainer.addEventListener('nx:forward', (e) => {
      const { body, senderId } = e.detail;
      const allRooms = getRooms().map(r => ({
        roomId: r.roomId,
        name: isDmRoom(r)
          ? (getDmPartner(r)?.name || getDisplayName(getDmPartner(r)?.userId) || r.name)
          : r.name || r.roomId,
      }));
      showForwardModal(allRooms, (targetRoomId) => {
        const senderName = getDisplayName(senderId);
        const forwardText = '↗️ Forwarded from ' + senderName + ':' + String.fromCharCode(10) + body;
        sendMessage(targetRoomId, forwardText).then(() => {
          showToast('Message forwarded', 'success');
        }).catch(err => showToast('Forward failed: ' + err.message, 'error'));
      });
    });

    // Wire emoji reaction events
    msgContainer.addEventListener('nx:react', (e) => {
      const { eventId, emoji } = e.detail;
      const room = getRoom(roomId);
      if (!room) return;
      const reactions = getReactionsForEvent(room, eventId);
      const myId = getMyUserId();

      // Check if user already reacted with THIS emoji
      const sameEmoji = reactions[emoji]?.find(r => r.userId === myId);
      if (sameEmoji) {
        redactEvent(roomId, sameEmoji.eventId).catch(console.error);
        return;
      }

      // Check if user already reacted with ANY emoji — remove it first
      for (const [e, users] of Object.entries(reactions)) {
        const old = users.find(u => u.userId === myId);
        if (old) {
          redactEvent(roomId, old.eventId).catch(console.error);
          break;
        }
      }

      sendReaction(roomId, eventId, emoji).catch(console.error);
    });
  }

  window.addEventListener('nx:reply-cancelled', () => {
    state.replyingTo = null;
  }, { once: true });

  // Wire file upload
  const fileInput = document.getElementById('file-input');
  if (fileInput) {
    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      fileInput.value = '';
      showFileSendPreview(file,
        async (confirmedFile) => { await handleFileUpload(roomId, confirmedFile); },
        () => {}
      );
    };
  }

  // Wire voice recorder
  initVoiceRecorder(async (blob, durationMs) => {
    setFileUploadProgress(true, 'Voice message');
    try {
      await sendVoiceMessage(roomId, blob, durationMs);
      showToast('Voice message sent', 'success');
    } catch (err) {
      showToast('Failed to send voice message: ' + err.message, 'error');
    } finally {
      setFileUploadProgress(false);
    }
  });

  // Wire poll button
  document.getElementById('poll-btn')?.addEventListener('click', () => {
    showPollCreator(async (question, options, kind, pollType) => {
      try {
        const maxSelections = pollType === 'multi' ? options.length : 1;
        await sendPoll(roomId, question, options, kind, maxSelections);
        showToast('Poll created', 'success');
      } catch (err) {
        showToast('Failed to create poll: ' + err.message, 'error');
      }
    });
  });

  // Wire GIF/Sticker button
  document.getElementById('gif-btn')?.addEventListener('click', (e) => {
    e.stopPropagation();
    if (document.getElementById('gif-picker')) {
      document.getElementById('gif-picker').remove();
      return;
    }
    showGifStickerPicker(async (url, type) => {
      try {
        if (type === 'gif') {
          // Send GIF as external URL message directly
          await sendExternalMedia(roomId, url, 'image/gif');
        } else {
          // Stickers: fetch via proxy and upload to Matrix media
          // Use URL directly - proxy handles the original s3 URL
          const proxyUrl = url.startsWith('/api/sticker-proxy')
            ? '/api/sticker-proxy?url=' + encodeURIComponent(decodeURIComponent(url.split('?url=')[1] || url))
            : url.includes('s3.getstickerpack.com')
              ? '/api/sticker-proxy?url=' + encodeURIComponent(url)
              : url;
          const res2 = await fetch(proxyUrl);
          const blob2 = await res2.blob();
          const ext = url.endsWith('.gif') ? 'gif' : 'png';
          const file2 = new File([blob2], 'sticker.' + ext, { type: blob2.type || 'image/' + ext });
          setFileUploadProgress(true, 'Sticker');
          const { content_uri } = await uploadFile(file2);
          await sendFile(roomId, file2, content_uri);
          setFileUploadProgress(false);
        }
      } catch (err) {
        setFileUploadProgress(false);
        showToast('Failed to send: ' + err.message, 'error');
      }
    });
  });

  _updateRoomTicks(room);
  _updateThreadBadges(roomId);

  // Wire disappearing messages + delete chat from header menu
  _wireRoomHeaderMenu(roomId);

  // Wire schedule button
  document.getElementById('schedule-btn')?.addEventListener('click', () => {
    const textarea = document.getElementById('message-input');
    const text = textarea?.value?.trim() || '';
    if (!text) {
      showToast('Type a message first to schedule it', 'error');
      return;
    }
    showScheduleModal(roomId, text, state.replyingTo?.eventId || null, async (scheduledAt) => {
      try {
        const jwt = await getNexAIJwt();
        const matrixToken = localStorage.getItem('nx_access_token') || '';
        const res = await fetch('/api/scheduled/create', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${jwt}`,
            'X-Matrix-Token': matrixToken || '',
          },
          body: JSON.stringify({ roomId, message: text, scheduledAt, replyTo: state.replyingTo?.eventId || null }),
        });
        const data = await res.json();
        if (data.success) {
          const d = new Date(data.scheduledAt);
          const fmt = d.toLocaleString([], { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' });
          showToast(`Scheduled for ${fmt}`, 'success');
          textarea.value = '';
          textarea.style.height = 'auto';
          state.replyingTo = null;
          document.getElementById('reply-preview')?.remove();
        } else {
          showToast(data.error || 'Failed to schedule', 'error');
        }
      } catch (err) {
        console.error('[schedule] error:', err);
        showToast('Failed to schedule message: ' + err.message, 'error');
      }
    });
  });

  // Wire call buttons
  document.getElementById('voice-call-btn')?.addEventListener('click', () => {
    _startCall(roomId, false);
  });
  document.getElementById('post-btn')?.addEventListener('click', () => {
    showPostComposer(async (title, body) => {
      try {
        await sendPost(roomId, title, body);
        showToast('Post published', 'success');
      } catch (err) {
        showToast('Failed: ' + err.message, 'error');
      }
    });
  });
  document.getElementById('video-call-btn')?.addEventListener('click', () => {
    _startCall(roomId, true);
  });

  // Render initial reactions for visible messages
  setTimeout(() => {
    const container = document.getElementById('messages-container');
    if (!container) return;
    container.querySelectorAll('.message-row[data-event-id]').forEach(el => {
      const eventId = el.dataset.eventId;
      if (!eventId || eventId.startsWith('~')) return;
      const reactions = getReactionsForEvent(room, eventId);
      if (Object.keys(reactions).length === 0) return;
      updateMessageReactions(eventId, reactions, getMyUserId(), (emoji, myReactionEventId) => {
        if (myReactionEventId) {
          redactEvent(state.activeRoomId, myReactionEventId).catch(console.error);
        } else {
          sendReaction(state.activeRoomId, eventId, emoji).catch(console.error);
        }
      });
    });
  }, 100);
}

/* ──────────────────────────────────────────────
   Send message
─────────────────────────────────────────────── */
async function handleSendMessage(roomId, text) {
  // Handle slash commands
  if (text.startsWith('/')) {
    const handled = await handleSlashCommand(roomId, text);
    if (handled) return;
  }

  // If offline, queue the message
  if (!navigator.onLine) {
    addToOfflineQueue(roomId, text, state.replyingTo?.eventId || null);
    state.replyingTo = null;
    setReplyingTo(null);
    return;
  }

  try {
    const replyTo = state.replyingTo;
    if (replyTo) {
      state.replyingTo = null;
      setReplyingTo(null);
      await sendMessage(roomId, text, replyTo);
    } else {
      await sendMessage(roomId, text);
    }
  } catch (err) {
    console.error('[send] Error:', err);
    // If network error, queue for retry
    if (!navigator.onLine || err.message?.includes('network') || err.message?.includes('fetch')) {
      addToOfflineQueue(roomId, text, state.replyingTo?.eventId || null);
    } else {
      showToast('Failed to send message. Please try again.', 'error');
    }
  }
}

async function handleSlashCommand(roomId, text) {
  const parts = text.trim().split(/\s+/);
  const cmd = parts[0].toLowerCase();
  const args = text.slice(cmd.length).trim();

  switch (cmd) {
    case '/shrug':
      await sendMessage(roomId, '¯\_(ツ)_/¯');
      return true;

    case '/tableflip':
      await sendMessage(roomId, '(╯°□°）╯︵ ┻━┻');
      return true;

    case '/me':
      if (!args) { showToast('Usage: /me [action]', 'error'); return true; }
      await sendMessage(roomId, `_${args}_`);
      return true;

    case '/clear':
      return true; // just clears the input

    case '/status':
      if (!args) { showToast('Usage: /status [message]', 'error'); return true; }
      try {
        await setPresence('online', args);
        showToast(`Status set: ${args}`, 'success');
      } catch (e) {
        showToast('Failed to set status', 'error');
      }
      return true;

    case '/remind':
      if (!args) { showToast('Usage: /remind [time] [message]\nExample: /remind 30m Check the build', 'error'); return true; }
      // Parse time: 30m, 1h, 2d
      const timeMatch = args.match(/^(\d+)(m|h|d)\s+(.+)$/i);
      if (!timeMatch) { showToast('Usage: /remind 30m [message] or /remind 2h [message]', 'error'); return true; }
      const [,num, unit, msg] = timeMatch;
      const ms = unit === 'm' ? num * 60000 : unit === 'h' ? num * 3600000 : num * 86400000;
      const scheduledAt = new Date(Date.now() + parseInt(ms)).toISOString();
      try {
        const jwt = await getNexAIJwt();
        const matrixToken = localStorage.getItem('nx_access_token') || '';
        const res = await fetch('/api/scheduled/create', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${jwt}`, 'X-Matrix-Token': matrixToken },
          body: JSON.stringify({ roomId, message: msg, scheduledAt }),
        });
        const data = await res.json();
        if (data.success) {
          const d = new Date(scheduledAt);
          showToast(`⏰ Reminder set for ${d.toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}`, 'success');
        } else {
          showToast('Failed to set reminder', 'error');
        }
      } catch (e) {
        showToast('Failed to set reminder', 'error');
      }
      return true;

    case '/poll':
      if (!args) { showToast('Usage: /poll Question | Option1 | Option2', 'error'); return true; }
      const pollParts = args.split('|').map(p => p.trim()).filter(Boolean);
      if (pollParts.length < 3) { showToast('Need at least: /poll Question | Option1 | Option2', 'error'); return true; }
      const [question, ...options] = pollParts;
      try {
        await sendPoll(roomId, question, options, 'org.matrix.msc3381.poll.kind.disclosed');
        showToast(t('toast.poll_created'), 'success');
      } catch (e) {
        showToast('Failed to create poll', 'error');
      }
      return true;

    case '/giphy':
      if (!args) { showToast('Usage: /giphy [search term]', 'error'); return true; }
      // Trigger giphy search with the term
      window._slashGiphySearch = args;
      document.getElementById('gif-btn')?.click();
      return true;

    default:
      return false; // not a slash command, send as normal
  }
}

function _updateRoomTicks(room) {
  if (!room) return;
  const myId = getMyUserId();
  const members = room.getJoinedMembers().filter(m => m.userId !== myId);
  if (!members.length) return;

  const timeline = room.getLiveTimeline().getEvents();

  // Collect per-member receipt timestamps — only OTHER members (not me)
  const memberReadTs = [];
  for (const member of members) {
    try {
      const receipt = room.getLastUnthreadedReceiptFor(member.userId);
      // Receipt can be { ts } or { data: { ts } } depending on SDK version
      const ts = receipt?.ts || receipt?.data?.ts || receipt?.event?.ts || 0;
      if (ts > 0) memberReadTs.push(ts);
    } catch (_) {}
  }

  const minReadTs = memberReadTs.length > 0 && memberReadTs.length >= members.length
    ? Math.min(...memberReadTs) : 0;
  const maxReadTs = memberReadTs.length > 0 ? Math.max(...memberReadTs) : 0;

  for (const event of timeline) {
    const id = event.getId();
    if (!id || id.startsWith('~') || event.getSender() !== myId) continue;
    if (event.getType() !== 'm.room.message') continue;
    const el = document.querySelector('.message-status[data-event-id="' + id + '"]');
    if (!el) continue;
    const evTs = event.getTs();
    const isRead = minReadTs > 0 && evTs <= minReadTs;
    const isDelivered = maxReadTs > 0 && evTs <= maxReadTs;
    if (el.title === 'Read by…' && !isRead) continue;
    const status = isRead ? 'read' : isDelivered ? 'delivered' : 'sent';
    updateMessageTick(id, status, isRead ? (eventId, anchorEl) => {
      const evObj = timeline.find(e => e.getId() === eventId);
      if (!evObj) return;
      const readers = getReadByList(room, evObj.getTs());
      showReadByPopup(readers, anchorEl);
    } : null);
    if (isRead) {
      const readers = getReadByList(room, event.getTs());
      updateReceiptAvatars(id, readers);
    } else {
      updateReceiptAvatars(id, []);
    }
  }
}

/* ──────────────────────────────────────────────
   File upload handler
─────────────────────────────────────────────── */
async function handleFileUpload(roomId, file) {
  const MAX_SIZE = 50 * 1024 * 1024; // 50MB
  if (file.size > MAX_SIZE) {
    showToast('File too large (max 50MB)', 'error');
    return;
  }

  setFileUploadProgress(true, file.name);
  try {
    const { content_uri } = await uploadFile(file);
    await sendFile(roomId, file, content_uri);
    showToast('File sent', 'success');
  } catch (err) {
    console.error('[file] Upload error:', err);
    showToast('Upload failed: ' + err.message, 'error');
  } finally {
    setFileUploadProgress(false);
  }
}

/* ──────────────────────────────────────────────
   New room / DM modal
─────────────────────────────────────────────── */
function openNewRoomModal() {
  renderNewRoomModal(handleCreateRoom, null);
}

async function handleCreateRoom({ type, userId: targetUserId, name, text, roomIds, roomId, label }) {
  try {
    if (type === 'dm') {
      const domain = getHomeserverDomain();
      const fullId = buildUserId(targetUserId, domain);
      const newRoomId = await createRoom(null, true, [fullId]);
      await new Promise(r => setTimeout(r, 500));
      renderRoomList(getRooms(), newRoomId, selectRoom);
      selectRoom(newRoomId);
      showToast('Conversation started', 'success');

    } else if (type === 'channel') {
      const newRoomId = await createRoom(name, false, []);
      await new Promise(r => setTimeout(r, 500));
      renderRoomList(getRooms(), newRoomId, selectRoom);
      selectRoom(newRoomId);
      showToast('#' + name + ' created', 'success');

    } else if (type === 'broadcast') {
      // Send message to all selected rooms
      let sent = 0;
      for (const rid of roomIds) {
        try {
          await sendMessage(rid, text);
          sent++;
        } catch (_) {}
      }
      showToast('📢 Broadcast sent to ' + sent + ' room' + (sent === 1 ? '' : 's'), 'success');
      // Save to broadcast history
      const history = JSON.parse(localStorage.getItem('nx_broadcast_lists') || '[]');
      history.unshift({ text, roomIds, ts: Date.now() });
      localStorage.setItem('nx_broadcast_lists', JSON.stringify(history.slice(0, 20)));

    } else if (type === 'guest') {
      const token = localStorage.getItem('nx_nexai_token');
      const res = await fetch('/api/guest/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify({ roomId, label })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);

      // Also invite the guest user to the room via Matrix if needed
      showToast('🔗 Guest invite link generated', 'success');
      return { inviteLink: data.inviteLink };
    }
  } catch (err) {
    throw new Error(err.message || 'Failed');
  }
}

function handleRedaction({ detail: { roomId, redactedEventId } }) {
  if (roomId !== state.activeRoomId) return;
  if (!redactedEventId) return;
  const container = document.getElementById('messages-container');
  if (!container) return;
  const row = container.querySelector(`.message-row[data-event-id="${redactedEventId}"]`);
  if (row) {
    const bodyEl = row.querySelector('.message-bubble div:not(.reply-preview)');
    if (bodyEl) {
      bodyEl.textContent = 'Message deleted';
      bodyEl.style.opacity = '0.5';
      bodyEl.style.fontStyle = 'italic';
    }
  }
}

function handleReaction({ detail: { roomId } }) {
  if (roomId !== state.activeRoomId) return;
  const room = getRoom(roomId);
  if (!room) return;
  const container = document.getElementById('messages-container');
  if (!container) return;

  // Re-render any visible poll cards with fresh vote tallies
  container.querySelectorAll('.poll-card[data-poll-id]').forEach(card => {
    const pollId = card.dataset.pollId;
    const pollEvent = room.getLiveTimeline().getEvents().find(e => e.getId() === pollId);
    if (!pollEvent) return;
    const answers = pollEvent.getContent()?.['org.matrix.msc3381.poll']?.answers || [];
    const { tally, myVote, totalVotes, isClosed } = getPollResults(room, pollId, answers);
    const myId = getMyUserId();
    const newCard = buildPollCard(pollEvent, room, myId,
      (pid, aIds) => window._sendPollVote?.(roomId, pid, aIds),
      (pid) => window._endPoll?.(roomId, pid)
    );
    if (newCard) card.replaceWith(newCard);
  });

  // Only process message-row elements directly
  container.querySelectorAll('.message-row[data-event-id]').forEach(row => {
    const eventId = row.dataset.eventId;
    if (!eventId || eventId.startsWith('~')) return;
    const reactions = getReactionsForEvent(room, eventId);
    if (Object.keys(reactions).length === 0) {
      row.querySelector('.reaction-bar')?.remove();
      return;
    }
    updateMessageReactions(eventId, reactions, getMyUserId(), (emoji, myReactionEventId) => {
      if (myReactionEventId) {
        redactEvent(roomId, myReactionEventId).catch(console.error);
      } else {
        sendReaction(roomId, eventId, emoji).catch(console.error);
      }
    });
  });
}


/* ──────────────────────────────────────────────
   Notifications + Sound
─────────────────────────────────────────────── */
const NOTIF_SOUNDS = {
  message:  null, // AudioBuffer
  mention:  null,
  dm:       null,
};



// Pre-create AudioContext on first user gesture
let _audioCtx = null;
function initAudio() {
  if (_audioCtx) return;
  try {
    _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  } catch (_) {}
}
// Init on any user interaction
['click','keydown','touchstart'].forEach(evt =>
  document.addEventListener(evt, () => { initAudio(); if (_audioCtx?.state === 'suspended') _audioCtx.resume(); }, { once: false, passive: true })
);

function playTone(type) {
  const settings = getNotifSettings();
  if (!settings.soundEnabled) return;

  const customUrl = localStorage.getItem('nx_sound_' + type);
  if (customUrl) {
    const audio = new Audio(customUrl);
    audio.volume = settings.volume || 0.5;
    audio.play().catch(() => {});
    return;
  }

  // Try to init/resume audio context
  if (!_audioCtx) initAudio();
  if (!_audioCtx) return;
  if (_audioCtx.state === 'suspended') {
    _audioCtx.resume().catch(() => {});
    return; // can't play right now
  }

  try {
    const TONES = {
      message: { freq: 440, duration: 0.15 },
      mention: { freq: 880, duration: 0.25 },
      dm:      { freq: 660, duration: 0.18 },
    };
    const tone = TONES[type] || TONES.message;
    const vol  = settings.volume || 0.5;
    const osc  = _audioCtx.createOscillator();
    const gain = _audioCtx.createGain();
    osc.connect(gain);
    gain.connect(_audioCtx.destination);
    osc.frequency.value = tone.freq;
    osc.type = 'sine';
    gain.gain.setValueAtTime(vol, _audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, _audioCtx.currentTime + tone.duration);
    osc.start(_audioCtx.currentTime);
    osc.stop(_audioCtx.currentTime + tone.duration);
  } catch (_) {}
}

function getNotifSettings() {
  try { return JSON.parse(localStorage.getItem('nx_notif_settings') || '{}'); }
  catch (_) { return {}; }
}

function saveNotifSettings(settings) {
  localStorage.setItem('nx_notif_settings', JSON.stringify(settings));
}

async function requestNotifPermission() {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const result = await Notification.requestPermission();
  return result === 'granted';
}

function showBrowserNotif(title, body, roomId) {
  const settings = getNotifSettings();
  if (!settings.browserEnabled) return;
  // if (document.hasFocus()) return; // Uncomment to hide notifs when focused
  if (Notification.permission !== 'granted') return;

  const notif = new Notification(title, {
    body,
    icon: '/favicon.svg',
    tag: roomId,
    silent: true,
  });
  notif.onclick = () => {
    window.focus();
    notif.close();
  };
  setTimeout(() => notif.close(), 5000);
}

function handleIncomingMessage(roomId, event) {
  const myId = getMyUserId();
  if (event.getSender() === myId) return; // Don't notify for own messages

  const content = event.getContent();
  const body = content?.body || '';
  if (!body) return;

  const room = getRoom(roomId);
  const senderName = getDisplayName(event.getSender());
  const isMention = body.toLowerCase().includes('@' + myId.split(':')[0].slice(1).toLowerCase())
    || body.includes(myId);
  const isDm = room ? isDmRoom(room) : false;
  const isActiveRoom = roomId === state.activeRoomId;
  const isFocused = document.hasFocus();

  // Always play sound for mentions, otherwise only when not focused on that room
  // Skip all sounds when DND is active
  if (!window._dndActive) {
    if (isMention) {
      playTone('mention');
    } else if (!isActiveRoom || !isFocused) {
      if (isDm) playTone('dm');
      else playTone('message');
    }
  }

  // Browser notification — only when not focused or not active room
  if (!isActiveRoom || !isFocused) {
    showBrowserNotif(
      isMention ? '🔔 ' + senderName + ' mentioned you' : senderName,
      body.slice(0, 100),
      roomId
    );
  }
}

/* ──────────────────────────────────────────────
   NexAI — handle in-chat AI requests
─────────────────────────────────────────────── */
async function handleNexAIRequest(roomId, userMessage, callbacks = {}) {
  if (!isNexAIReady()) {
    const { accessToken } = getStoredTokens();
    const ok = accessToken ? await initNexAI(accessToken) : false;
    if (!ok) {
      callbacks.onError?.('NexAI unavailable — please refresh');
      showToast('NexAI unavailable — please refresh', 'error');
      return;
    }
  }

  const room = getRoom(roomId);
  if (!room) return;

  // Build room context from last 15 messages
  const timeline = getTimeline(room, 15);
  const roomContext = timeline
    .filter(e => e.getType() === 'm.room.message' && !e.isDecryptionFailure?.())
    .map(e => {
      const m = formatMessage(e);
      return '[' + getDisplayName(m.senderId) + ']: ' + m.body;
    }).join(String.fromCharCode(10));

  const roomName = isDmRoom(room) ? 'Direct Message' : (room.name || roomId);
  const domain   = getHomeserverDomain();

  // Use panel conversation history if provided
  const history = callbacks.history?.length
    ? callbacks.history
    : [{ role: 'user', content: userMessage }];

  setNexAIButtonState('loading');
  let fullText = '';

  try {
    await nexaiChat({
      messages:      history,
      workspaceName: domain,
      channelName:   roomName,
      roomContext:   roomContext,
      onChunk: (chunk) => {
        fullText += chunk;
        callbacks.onChunk?.(fullText);
      },
    });
    callbacks.onDone?.(fullText);
  } catch (err) {
    if (err.message === 'SESSION_EXPIRED') {
      clearNexAIToken();
      showToast('NexAI session expired — reinitialising…', 'error');
      const { accessToken } = getStoredTokens();
      if (accessToken) initNexAI(accessToken);
    }
    callbacks.onError?.(err.message || 'NexAI error');
  } finally {
    setNexAIButtonState('idle');
  }
}


/* ──────────────────────────────────────────────
   Room header menu — disappearing messages + delete
─────────────────────────────────────────────── */
function _wireRoomHeaderMenu(roomId) {
  // Add ⋯ menu button to chat header if not already there
  const headerActions = document.querySelector('.chat-header-actions');
  if (!headerActions || document.getElementById('room-menu-btn')) return;

  const menuBtn = document.createElement('button');
  menuBtn.className = 'icon-btn';
  menuBtn.id = 'room-menu-btn';
  menuBtn.title = 'Room settings';
  menuBtn.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>';
  headerActions.appendChild(menuBtn);

  menuBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    _showRoomMenu(roomId, menuBtn);
  });
}

async function _showGuestInvitePanel(roomId) {
  document.getElementById('guest-invite-panel')?.remove();
  const room = getRoom(roomId);
  const roomName = room?.name || roomId;

  const panel = document.createElement('div');
  panel.id = 'guest-invite-panel';
  panel.className = 'search-panel';
  panel.innerHTML = `
    <div class="search-panel-header">
      <span style="font-weight:600;font-size:14px;">🔗 Invite Guest</span>
      <button class="icon-btn" id="guest-panel-close">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>
    <div style="padding:12px 16px;">
      <div style="font-size:12px;color:var(--text-secondary);margin-bottom:12px;">
        Generate a one-time invite link for <strong style="color:var(--text-primary);">${escHtml(roomName)}</strong>. Link expires in 24 hours.
      </div>
      <label style="font-size:12px;color:var(--text-secondary);display:block;margin-bottom:4px;">Guest name / label (optional)</label>
      <input type="text" class="topic-input" id="guest-panel-label" placeholder="e.g. Client, Partner…" style="width:100%;margin-bottom:12px;box-sizing:border-box;" />
      <button class="send-btn" id="guest-panel-generate" style="width:100%;padding:8px;">Generate Invite Link</button>
      <div id="guest-panel-result" style="display:none;margin-top:12px;">
        <div style="font-size:12px;color:var(--text-secondary);margin-bottom:6px;">Share this link:</div>
        <div style="display:flex;gap:8px;">
          <input type="text" class="topic-input" id="guest-panel-link" readonly style="flex:1;font-size:11px;" />
          <button class="pdf-nav-btn" id="guest-panel-copy">Copy</button>
        </div>
        <div style="font-size:11px;color:var(--text-secondary);margin-top:6px;">Guest will get access to this room only. Remove them anytime via Manage Members.</div>
      </div>
    </div>`;

  panel.querySelector('#guest-panel-close').addEventListener('click', () => panel.remove());

  panel.querySelector('#guest-panel-generate').addEventListener('click', async () => {
    const label = panel.querySelector('#guest-panel-label').value.trim();
    const btn = panel.querySelector('#guest-panel-generate');
    btn.disabled = true;
    btn.textContent = 'Generating…';
    try {
      const token = localStorage.getItem('nx_nexai_token');
      const res = await fetch('/api/guest/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify({ roomId, label })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      panel.querySelector('#guest-panel-result').style.display = 'block';
      panel.querySelector('#guest-panel-link').value = data.inviteLink;
      panel.querySelector('#guest-panel-copy').addEventListener('click', () => {
        navigator.clipboard.writeText(data.inviteLink);
        showToast('🔗 Invite link copied!', 'success');
      });
      btn.textContent = 'Generate New Link';
      btn.disabled = false;
    } catch (err) {
      showToast('Failed: ' + err.message, 'error');
      btn.disabled = false;
      btn.textContent = 'Generate Invite Link';
    }
  });

  const container = document.getElementById('chat-container') || document.body;
  container.appendChild(panel);
}

function _showMembersPanel(roomId) {
  document.getElementById('members-panel')?.remove();
  const room = getRoom(roomId);
  if (!room) return;
  const myId = getMyUserId();
  const members = room.getJoinedMembers().filter(m => m.userId !== myId);

  const panel = document.createElement('div');
  panel.id = 'members-panel';
  panel.className = 'search-panel';

  panel.innerHTML = `
    <div class="search-panel-header">
      <span style="font-weight:600;font-size:14px;">👥 Members (${members.length})</span>
      <button class="icon-btn" id="members-panel-close">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>
    <div class="search-panel-results" id="members-list"></div>`;

  panel.querySelector('#members-panel-close').addEventListener('click', () => panel.remove());

  const list = panel.querySelector('#members-list');
  for (const member of members) {
    const name = member.name || member.userId.split(':')[0].slice(1);
    const avatarUrl = member.getMxcAvatarUrl();
    const avatarHtml = avatarUrl
      ? `<div style="width:32px;height:32px;border-radius:50%;background-image:url(${window._mxcToHttp ? window._mxcToHttp(avatarUrl) : ''});background-size:cover;flex-shrink:0;"></div>`
      : `<div style="width:32px;height:32px;border-radius:50%;background:var(--accent);display:flex;align-items:center;justify-content:center;color:#fff;font-size:13px;font-weight:600;flex-shrink:0;">${name[0].toUpperCase()}</div>`;

    const item = document.createElement('div');
    item.className = 'search-result-item';
    item.style.cssText = 'display:flex;align-items:center;gap:10px;';
    item.innerHTML = `
      ${avatarHtml}
      <div style="flex:1;min-width:0;">
        <div style="font-size:13px;color:var(--text-primary);font-weight:500;">${escHtml(name)}</div>
        <div style="font-size:11px;color:var(--text-secondary);">${escHtml(member.userId)}</div>
      </div>
      <button class="rsm-remove-btn member-kick-btn" data-user-id="${member.userId}" style="color:#ef4444;border-color:#ef4444;white-space:nowrap;">
        Kick
      </button>`;

    item.querySelector('.member-kick-btn').addEventListener('click', async () => {
      if (!confirm('Remove ' + name + ' from this room?')) return;
      try {
        await kickUser(roomId, member.userId);
        item.remove();
        showToast(name + ' removed', 'success');
        if (!list.querySelectorAll('.search-result-item').length) {
          list.innerHTML = '<div class="search-panel-empty">No other members.</div>';
        }
      } catch (err) {
        showToast('Failed: ' + err.message, 'error');
      }
    });
    list.appendChild(item);
  }

  if (!members.length) {
    list.innerHTML = '<div class="search-panel-empty">No other members in this room.</div>';
  }

  const container = document.getElementById('chat-container') || document.body;
  container.appendChild(panel);
}

async function _showRoomMenu(roomId, anchor) {
  document.getElementById('room-menu-popup')?.remove();

  const room   = getRoom(roomId);
  const isAdmin = isRoomAdmin(room);
  const ttl    = getDisappearingMessageTtl(room);

  const TIMERS = [
    { label: 'Off',      value: 0 },
    { label: '30 sec',   value: 30 * 1000 },
    { label: '5 min',    value: 5 * 60 * 1000 },
    { label: '1 hour',   value: 60 * 60 * 1000 },
    { label: '24 hours', value: 24 * 60 * 60 * 1000 },
    { label: '7 days',   value: 7 * 24 * 60 * 60 * 1000 },
    { label: '30 days',  value: 30 * 24 * 60 * 60 * 1000 },
  ];

  const currentTimer = TIMERS.find(t => t.value === ttl) || TIMERS[0];

  const menu = document.createElement('div');
  menu.id = 'room-menu-popup';
  menu.className = 'msg-action-menu';
  menu.style.cssText = 'min-width:220px;z-index:300;';

  // Notification settings section
  const notifHeader = document.createElement('div');
  notifHeader.style.cssText = 'padding:6px 12px 4px;font-size:11px;color:var(--text-muted);font-weight:600;text-transform:uppercase;letter-spacing:0.05em;';
  notifHeader.textContent = '🔔 Notifications';
  menu.appendChild(notifHeader);

  const notifSetting = await getRoomNotifSetting(roomId);
  const NOTIF_OPTIONS = [
    { label: 'All messages', value: 'all', icon: '🔔' },
    { label: 'Mentions only', value: 'mentions', icon: '🔕' },
    { label: 'Mute', value: 'mute', icon: '🚫' },
  ];
  for (const opt of NOTIF_OPTIONS) {
    const btn = document.createElement('button');
    btn.className = 'msg-action-item' + (opt.value === notifSetting ? ' active' : '');
    btn.style.cssText = opt.value === notifSetting ? 'background:var(--bg-elevated);' : '';
    btn.innerHTML = `<span>${opt.value === notifSetting ? '✓' : ''}</span><span>${opt.icon} ${opt.label}</span>`;
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      menu.remove();
      await setRoomNotifSetting(roomId, opt.value);
      showToast(
        opt.value === 'mute' ? '🚫 Room muted' :
        opt.value === 'mentions' ? '🔕 Mentions only' : '🔔 All notifications on',
        'success'
      );
    });
    menu.appendChild(btn);
  }

  const notifDiv = document.createElement('div');
  notifDiv.className = 'dropdown-divider';
  menu.appendChild(notifDiv);

  // Members section (admin only)
  if (isAdmin) {
    const membersHeader = document.createElement('div');
    membersHeader.style.cssText = 'padding:6px 12px 4px;font-size:11px;color:var(--text-muted);font-weight:600;text-transform:uppercase;letter-spacing:0.05em;';
    membersHeader.textContent = '👥 Members';
    menu.appendChild(membersHeader);

    const membersBtn = document.createElement('button');
    membersBtn.className = 'msg-action-item';
    membersBtn.innerHTML = '<span>👤</span><span>Manage Members</span>';
    membersBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      menu.remove();
      _showMembersPanel(roomId);
    });
    menu.appendChild(membersBtn);

    const guestBtn = document.createElement('button');
    guestBtn.className = 'msg-action-item';
    guestBtn.innerHTML = '<span>🔗</span><span>Invite Guest</span>';
    guestBtn.addEventListener('click', async (e) => {
      e.stopPropagation();
      menu.remove();
      _showGuestInvitePanel(roomId);
    });
    menu.appendChild(guestBtn);

    const membersDiv = document.createElement('div');
    membersDiv.className = 'dropdown-divider';
    menu.appendChild(membersDiv);
  }

  // Disappearing messages section
  const disappearHeader = document.createElement('div');
  disappearHeader.style.cssText = 'padding:6px 12px 4px;font-size:11px;color:var(--text-muted);font-weight:600;text-transform:uppercase;letter-spacing:0.05em;';
  disappearHeader.textContent = '⏱ Disappearing Messages';
  menu.appendChild(disappearHeader);

  for (const timer of TIMERS) {
    const btn = document.createElement('button');
    btn.className = 'msg-action-item' + (timer.value === ttl ? ' active' : '');
    btn.style.cssText = timer.value === ttl ? 'background:var(--bg-elevated);' : '';
    btn.innerHTML = `<span>${timer.value === ttl ? '✓' : ''}</span><span>${timer.label}</span>`;
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      menu.remove();
      try {
        await setDisappearingMessages(roomId, timer.value);
        showToast(timer.value ? `Messages disappear after ${timer.label}` : 'Disappearing messages off', 'success');
        // Immediately purge if turning on
        if (timer.value) await purgeExpiredMessages(roomId).catch(() => {});
      } catch (err) {
        showToast('Failed: ' + err.message, 'error');
      }
    });
    menu.appendChild(btn);
  }

  // Divider
  const div1 = document.createElement('div');
  div1.className = 'dropdown-divider';
  menu.appendChild(div1);

  // Delete section
  const deleteHeader = document.createElement('div');
  deleteHeader.style.cssText = 'padding:6px 12px 4px;font-size:11px;color:var(--text-muted);font-weight:600;text-transform:uppercase;letter-spacing:0.05em;';
  deleteHeader.textContent = '🗑 Chat Management';
  menu.appendChild(deleteHeader);

  // Clear my messages — available to everyone
  const clearMineBtn = document.createElement('button');
  clearMineBtn.className = 'msg-action-item';
  clearMineBtn.innerHTML = '<span>🧹</span><span>Clear my messages</span>';
  clearMineBtn.addEventListener('click', async (e) => {
    e.stopPropagation();
    menu.remove();
    if (!confirm('Delete all your messages in this chat?')) return;
    try {
      showToast('Clearing your messages…', 'success');
      await clearMyMessages(roomId);
      showToast('Your messages cleared', 'success');
    } catch (err) {
      showToast('Failed: ' + err.message, 'error');
    }
  });
  menu.appendChild(clearMineBtn);

  // Empty room — admin only
  if (isAdmin) {
    const emptyBtn = document.createElement('button');
    emptyBtn.className = 'msg-action-item danger';
    emptyBtn.innerHTML = '<span>💣</span><span>Empty room (all messages)</span>';
    emptyBtn.addEventListener('click', async (e) => {
      e.stopPropagation();
      menu.remove();
      if (!confirm('Delete ALL messages in this room for everyone? This cannot be undone.')) return;
      try {
        showToast('Emptying room…', 'success');
        // Use backend purge API for complete history wipe
        const { accessToken } = getStoredTokens();
        const res = await fetch('/api/admin/rooms/' + encodeURIComponent(roomId) + '/empty', {
          method: 'POST',
          headers: { Authorization: 'Bearer ' + accessToken },
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'Purge failed');
        showToast('Room history purged', 'success');
      } catch (err) {
        showToast('Failed: ' + err.message, 'error');
      }
    });
    menu.appendChild(emptyBtn);
  }

  // Leave room
  const leaveBtn = document.createElement('button');
  leaveBtn.className = 'msg-action-item danger';
  leaveBtn.innerHTML = '<span>🚪</span><span>Leave conversation</span>';
  leaveBtn.addEventListener('click', async (e) => {
    e.stopPropagation();
    menu.remove();
    if (!confirm('Leave this conversation?')) return;
    try {
      await leaveRoom(roomId);
      showToast('Left conversation', 'success');
      // Reset to no room selected
      document.getElementById('chat-area').innerHTML = '';
    } catch (err) {
      showToast('Failed: ' + err.message, 'error');
    }
  });
  menu.appendChild(leaveBtn);

  // Position below anchor
  const rect = anchor.getBoundingClientRect();
  menu.style.top  = (rect.bottom + 4) + 'px';
  menu.style.right = (window.innerWidth - rect.right) + 'px';
  menu.style.left = 'auto';
  document.body.appendChild(menu);

  setTimeout(() => {
    document.addEventListener('click', () => menu.remove(), { once: true });
  }, 10);
}

/* ──────────────────────────────────────────────
   Logout
─────────────────────────────────────────────── */
/* ──────────────────────────────────────────────
   Mobile Sidebar
─────────────────────────────────────────────── */
function _initMobileSidebar() {
  let overlay = document.getElementById('sidebar-overlay');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'sidebar-overlay';
    overlay.className = 'sidebar-overlay';
    document.body.appendChild(overlay);
  }

  const sidebar = document.querySelector('.sidebar');
  const openSidebar  = () => { sidebar?.classList.add('mobile-open');    overlay.classList.add('visible'); };
  const closeSidebar = () => { sidebar?.classList.remove('mobile-open'); overlay.classList.remove('visible'); };

  overlay.addEventListener('click', closeSidebar);
  document.addEventListener('click', (e) => { if (e.target.closest('#mobile-menu-btn')) openSidebar(); });

  // Swipe right from left edge to open, swipe left to close
  let tx = 0, ty = 0;
  document.addEventListener('touchstart', (e) => { tx = e.touches[0].clientX; ty = e.touches[0].clientY; }, { passive: true });
  document.addEventListener('touchend', (e) => {
    const dx = e.changedTouches[0].clientX - tx;
    const dy = Math.abs(e.changedTouches[0].clientY - ty);
    if (tx < 40 && dx > 60 && dy < 80) openSidebar();
    if (dx < -60 && dy < 80 && sidebar?.classList.contains('mobile-open')) closeSidebar();
  }, { passive: true });
}

function escHtml(str) {
  if (!str) return '';
  return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

/* ──────────────────────────────────────────────
   Thread badge updater
─────────────────────────────────────────────── */
function _updateThreadBadges(roomId) {
  const room = getRoom(roomId);
  if (!room) return;
  const container = document.getElementById('messages-container');
  if (!container) return;

  container.querySelectorAll('[data-event-id]').forEach(el => {
    const eventId = el.dataset.eventId;
    if (!eventId || eventId.startsWith('~')) return;
    const badge = document.getElementById('thread-badge-' + eventId);
    if (!badge) return;

    const summary = getThreadSummary(room, eventId);
    if (!summary) { badge.style.display = 'none'; return; }

    const senderName = getDisplayName(summary.lastReplySender).split(' ')[0];
    const preview = summary.lastReplyBody.slice(0, 40) + (summary.lastReplyBody.length > 40 ? '…' : '');
    badge.style.display = 'inline-flex';
    badge.innerHTML = `
      <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
      <span class="thread-badge-count">${summary.count} ${summary.count === 1 ? 'reply' : 'replies'}</span>
      <span class="thread-badge-preview">${escHtml(senderName)}: ${escHtml(preview)}</span>
    `;
    badge.onclick = () => {
      const room2 = getRoom(roomId);
      const event = room2?.getLiveTimeline().getEvents().find(e => e.getId() === eventId);
      if (!event) return;
      openThreadPanel(event, room2, getMyUserId(), async (rootId, text) => {
        try { await sendThreadReply(roomId, rootId, text); }
        catch (err) { showToast('Failed: ' + err.message, 'error'); }
      });
    };
  });
}


/* ──────────────────────────────────────────────
   Voice / Video Calls
─────────────────────────────────────────────── */
let _activeCallRoomId = null;
let _ringtoneAudio    = null;

async function _startCall(roomId, isVideo) {
  const room = getRoom(roomId);
  if (!room) return;
  const myProfile = await getMyProfile().catch(() => ({}));
  const displayName = myProfile.displayName || getMyDisplayName();
  const avatarUrl = myProfile.avatarUrl ? mxcToHttp(myProfile.avatarUrl) : null;
  const isDm    = isDmRoom(room);
  const partner = isDm ? getDmPartner(room) : null;
  const name    = isDm && partner ? (partner.name || getDisplayName(partner.userId)) : (room.name || room.roomId);
  const callUrl = getElementCallUrl(roomId, displayName, avatarUrl, isVideo);
  await sendCallInvite(roomId, isVideo).catch(() => {});
  _activeCallRoomId = roomId;
  const color    = _getAvatarColorCall(name);
  const initials = name.slice(0, 2).toUpperCase();
  openCallModal(callUrl, name, color, initials, async () => {
    _activeCallRoomId = null;
    document.getElementById('call-pip')?.remove();
    await sendCallEnd(roomId).catch(() => {});
  });
}

function _handleIncomingCall(roomId, event) {
  const room = getRoom(roomId);
  if (!room || _activeCallRoomId) return;
  const sender   = event.getSender();
  const name     = getDisplayName(sender);
  const callType = event.getContent()?.call_type || 'voice';
  const color    = _getAvatarColorCall(name);
  const initials = name.slice(0, 2).toUpperCase();
  _playRingtone();
  showIncomingCallBanner(name, color, initials, callType,
    async () => {
      _stopRingtone();
      const myProfile = await getMyProfile().catch(() => ({}));
      const displayName = myProfile.displayName || getMyDisplayName();
      const avatarUrl = myProfile.avatarUrl ? mxcToHttp(myProfile.avatarUrl) : null;
      const callUrl = getElementCallUrl(roomId, displayName, avatarUrl, callType === 'video');
      _activeCallRoomId = roomId;
      openCallModal(callUrl, name, color, initials, async () => {
        _activeCallRoomId = null;
        document.getElementById('call-pip')?.remove();
        await sendCallEnd(roomId).catch(() => {});
      });
    },
    () => { _stopRingtone(); }
  );
}

function _playRingtone() {
  _stopRingtone();
  const customUrl = localStorage.getItem('nx_sound_ringtone');
  if (customUrl) {
    const audio = new Audio(customUrl);
    audio.loop = true; audio.volume = 0.7;
    audio.play().catch(() => {});
    _ringtoneAudio = audio;
    return;
  }
  if (!_audioCtx) return;
  const play = () => {
    try {
      const osc = _audioCtx.createOscillator();
      const gain = _audioCtx.createGain();
      osc.connect(gain); gain.connect(_audioCtx.destination);
      osc.frequency.value = 880; osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, _audioCtx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, _audioCtx.currentTime + 0.5);
      osc.start(_audioCtx.currentTime);
      osc.stop(_audioCtx.currentTime + 0.5);
    } catch (_) {}
  };
  play();
  _ringtoneAudio = setInterval(play, 1200);
}

function _stopRingtone() {
  if (!_ringtoneAudio) return;
  if (typeof _ringtoneAudio === 'number') clearInterval(_ringtoneAudio);
  else { _ringtoneAudio.pause(); _ringtoneAudio.currentTime = 0; }
  _ringtoneAudio = null;
}

function _getAvatarColorCall(name) {
  const COLORS = ['#6c3ef4','#0ea5e9','#10b981','#f59e0b','#ef4444','#ec4899','#8b5cf6','#14b8a6'];
  let hash = 0;
  for (let i = 0; i < (name||'').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return COLORS[Math.abs(hash) % COLORS.length];
}



function getNexAIJwt() {
  return Promise.resolve(getNexAIToken());
}


/* ──────────────────────────────────────────────
   Push Notifications (VAPID)
─────────────────────────────────────────────── */
async function initPushNotifications() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.log('[push] not supported');
    return;
  }

  try {
    // Register service worker
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    console.log('[push] service worker registered');

    // Check permission
    if (Notification.permission === 'denied') return;

    // Get VAPID public key
    const keyRes = await fetch('/api/push/vapid-public-key');
    const { publicKey } = await keyRes.json();
    if (!publicKey) return;

    // Check if already subscribed
    let sub = await reg.pushManager.getSubscription();

    if (!sub) {
      // Only subscribe if notifications are granted
      if (Notification.permission !== 'granted') return;
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });
    }

    // Save subscription to server
    const jwt = await getNexAIJwt();
    const matrixToken = localStorage.getItem('nx_access_token') || '';
    await fetch('/api/push/subscribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwt}`,
        'X-Matrix-Token': matrixToken,
      },
      body: JSON.stringify(sub.toJSON())
    });

    console.log('[push] subscribed successfully');
  } catch (err) {
    console.error('[push] init error:', err);
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

async function requestPushPermission() {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted' && !window._dndActive) {
    await initPushNotifications();
    return true;
  }
  if (Notification.permission === 'denied') return false;
  const permission = await Notification.requestPermission();
  if (permission === 'granted') {
    await initPushNotifications();
    return true;
  }
  return false;
}


/* ──────────────────────────────────────────────
   Push Notification Trigger
─────────────────────────────────────────────── */
async function triggerPushForMessage(roomId, event) {
  try {
    const room = getRoom(roomId);
    if (!room) return;
    const senderId = event.getSender();
    const senderName = getDisplayName(senderId) || senderId.split(':')[0].slice(1);
    const roomName = room.name || 'NexChat';
    const content = event.getContent();
    const body = content?.body ||
      (content?.msgtype === 'm.image' ? '📷 Image' :
       content?.msgtype === 'm.audio' ? '🎤 Voice message' :
       content?.msgtype === 'm.file'  ? '📎 File' : 'New message');
    const members = room.getJoinedMembers().filter(m => m.userId !== senderId);
    for (const member of members) {
      fetch('/api/push/notify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-token': 'syt_bHJ1aXo_ZxwpXYkucmTEHgWmRwcC_0gKXBK'
        },
        body: JSON.stringify({
          userId: member.userId,
          senderName,
          roomName,
          body,
          roomId,
          eventId: event.getId()
        })
      }).catch(() => {});
    }
  } catch (err) {
    console.error('[push] trigger error:', err);
  }
}



/* ──────────────────────────────────────────────
   Offline Message Queue
─────────────────────────────────────────────── */
const OFFLINE_QUEUE_KEY = 'nx_offline_queue';

function getOfflineQueue() {
  try { return JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY) || '[]'); }
  catch { return []; }
}

function saveOfflineQueue(queue) {
  localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
}

function addToOfflineQueue(roomId, text, replyTo) {
  const queue = getOfflineQueue();
  queue.push({ roomId, text, replyTo: replyTo || null, ts: Date.now(), id: Math.random().toString(36).slice(2) });
  saveOfflineQueue(queue);
  showToast(t('toast.offline_queued'), 'error');
  // Register background sync
  navigator.serviceWorker?.ready.then(reg => {
    reg.active?.postMessage({ type: 'NX_REGISTER_SYNC' });
  }).catch(() => {});
  _updateOfflineIndicator();
}

let _flushingQueue = false;
async function flushOfflineQueue() {
  if (_flushingQueue) return;
  const queue = getOfflineQueue();
  if (!queue.length) return;
  if (!navigator.onLine) return;

  _flushingQueue = true;
  // Clear queue immediately to prevent duplicate sends
  saveOfflineQueue([]);

  const remaining = [];
  for (const msg of queue) {
    try {
      const replyTo = msg.replyTo ? { eventId: msg.replyTo } : null;
      if (replyTo) {
        await sendMessage(msg.roomId, msg.text, replyTo);
      } else {
        await sendMessage(msg.roomId, msg.text);
      }
    } catch (err) {
      remaining.push(msg);
    }
  }

  if (remaining.length) saveOfflineQueue(remaining);

  const sent = queue.length - remaining.length;
  if (sent > 0) {
    showToast(`📶 Back online — sent ${sent} queued message${sent > 1 ? 's' : ''}`, 'success');
  }
  _flushingQueue = false;
  _updateOfflineIndicator();
}

function _updateOfflineIndicator() {
  const queue = getOfflineQueue();
  let indicator = document.getElementById('offline-indicator');

  if (!navigator.onLine || queue.length > 0) {
    if (!indicator) {
      indicator = document.createElement('div');
      indicator.id = 'offline-indicator';
      indicator.style.cssText = `
        position: fixed; bottom: 16px; left: 50%; transform: translateX(-50%);
        background: rgba(239,68,68,0.9); color: white;
        font-size: 12px; font-weight: 600; padding: 6px 16px;
        border-radius: 999px; z-index: 1000;
        display: flex; align-items: center; gap: 6px;
        box-shadow: 0 4px 16px rgba(0,0,0,0.3);
        backdrop-filter: blur(8px);
      `;
      document.body.appendChild(indicator);
    }
    if (!navigator.onLine) {
      indicator.innerHTML = '📶 Offline' + (queue.length ? ` · ${queue.length} message${queue.length > 1 ? 's' : ''} queued` : '');
    } else if (queue.length > 0) {
      indicator.innerHTML = `⏳ Sending ${queue.length} queued message${queue.length > 1 ? 's' : ''}…`;
    }
  } else {
    indicator?.remove();
  }
}

function _initOfflineSync() {
  // Online/offline events
  window.addEventListener('online', () => {
    _updateOfflineIndicator();
    setTimeout(flushOfflineQueue, 1000); // small delay to ensure connection is stable
  });

  window.addEventListener('offline', () => {
    _updateOfflineIndicator();
  });

  // Service worker message — flush when SW triggers sync
  navigator.serviceWorker?.addEventListener('message', e => {
    if (e.data?.type === 'NX_FLUSH_QUEUE') flushOfflineQueue();
  });

  // Flush any pending messages on boot (once, with delay)
  if (navigator.onLine) {
    setTimeout(() => {
      const q = getOfflineQueue();
      if (q.length) flushOfflineQueue();
    }, 3000);
  }

  // Update indicator on boot
  _updateOfflineIndicator();
}


/* ──────────────────────────────────────────────
   Message Translation
─────────────────────────────────────────────── */
const ALL_LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', flag: '🇮🇹' },
  { code: 'pt', name: 'Portuguese', flag: '🇧🇷' },
  { code: 'ru', name: 'Russian', flag: '🇷🇺' },
  { code: 'zh', name: 'Chinese (Simplified)', flag: '🇨🇳' },
  { code: 'zh-tw', name: 'Chinese (Traditional)', flag: '🇹🇼' },
  { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
  { code: 'ko', name: 'Korean', flag: '🇰🇷' },
  { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
  { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
  { code: 'ur', name: 'Urdu', flag: '🇵🇰' },
  { code: 'tr', name: 'Turkish', flag: '🇹🇷' },
  { code: 'nl', name: 'Dutch', flag: '🇳🇱' },
  { code: 'pl', name: 'Polish', flag: '🇵🇱' },
  { code: 'sv', name: 'Swedish', flag: '🇸🇪' },
  { code: 'id', name: 'Indonesian', flag: '🇮🇩' },
  { code: 'th', name: 'Thai', flag: '🇹🇭' },
];

function _showTranslateLanguagePicker(eventId, roomId) {
  document.getElementById('translate-lang-picker')?.remove();

  const lastLang = localStorage.getItem('nx_translate_lang') || 'es';

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'translate-lang-picker';
  overlay.innerHTML = `
    <div class="modal" style="max-width:360px;">
      <h2 class="modal-title">🌐 Translate to…</h2>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;max-height:360px;overflow-y:auto;margin:12px 0;">
        ${ALL_LANGUAGES.map(l => `
          <div class="slash-item" data-lang="${escHtml(l.name)}" data-code="${l.code}" style="
            border-radius:8px;cursor:pointer;padding:8px 10px;
            border:2px solid ${l.code === lastLang ? 'var(--accent)' : 'transparent'};
          ">
            <span style="font-size:16px;">${l.flag}</span>
            <div class="slash-item-name" style="font-size:12px;">${l.name}</div>
          </div>
        `).join('')}
      </div>
      <div class="modal-actions">
        <button class="btn btn-secondary" id="trans-close">Cancel</button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add('visible'));

  overlay.querySelectorAll('[data-lang]').forEach(item => {
    item.addEventListener('click', () => {
      const lang = item.dataset.lang;
      localStorage.setItem('nx_translate_lang', item.dataset.code);
      overlay.remove();
      _doTranslateMessage(eventId, roomId, lang);
    });
  });

  document.getElementById('trans-close')?.addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });
}

async function _handleTranslateMessage(eventId, roomId) {
  // Show language picker first
  _showTranslateLanguagePicker(eventId, roomId);
}

async function _doTranslateMessage(eventId, roomId, targetLanguage) {
  const room = getRoom(roomId);
  if (!room) return;

  const event = room.getLiveTimeline().getEvents().find(e => e.getId() === eventId);
  if (!event) return;

  const text = event.getContent()?.body;
  if (!text) return;

  // Show loading state in message bubble
  const msgEl = document.querySelector('[data-event-id="' + eventId + '"]');
  const existingTranslation = msgEl?.querySelector('.msg-translation');
  if (existingTranslation) {
    // Toggle — remove if already showing
    existingTranslation.remove();
    return;
  }

  // Add loading indicator
  const loadingEl = document.createElement('div');
  loadingEl.className = 'msg-translation loading';
  loadingEl.textContent = '🌐 Translating…';
  loadingEl.style.cssText = 'font-size:12px;color:var(--text-muted);margin-top:4px;font-style:italic;';
  msgEl?.querySelector('.message-bubble')?.appendChild(loadingEl);

  try {
    const jwt = localStorage.getItem('nx_nexai_token');
    const res = await fetch('/api/nexai/translate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${jwt}` },
      body: JSON.stringify({ text, targetLanguage })
    });
    const data = await res.json();

    if (data.translated) {
      loadingEl.className = 'msg-translation';
      loadingEl.style.cssText = `
        font-size:12px;
        color:var(--text-secondary);
        margin-top:6px;
        padding:6px 8px;
        background:var(--bg-elevated);
        border-left:2px solid var(--accent);
        border-radius:0 4px 4px 0;
        line-height:1.4;
      `;
      loadingEl.innerHTML = `
        <span style="font-size:10px;color:var(--accent-text);font-weight:600;display:block;margin-bottom:2px;">🌐 ${targetLanguage}</span>
        ${escHtml(data.translated)}
      `;
    } else {
      loadingEl.remove();
      showToast('Translation failed', 'error');
    }
  } catch (err) {
    loadingEl.remove();
    showToast('Translation failed', 'error');
  }
}


/* ──────────────────────────────────────────────
   Favicon Badge Counter
─────────────────────────────────────────────── */
let _faviconCanvas = null;
let _faviconCtx = null;
let _originalFavicon = null;

function _initFavicon() {
  const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
  link.type = 'image/x-icon';
  link.rel = 'shortcut icon';
  document.head.appendChild(link);
  _originalFavicon = link.href;
}

function updateFaviconBadge(count) {
  const link = document.querySelector("link[rel*='icon']");
  if (!link) return;

  if (count <= 0) {
    // Restore original favicon
    link.href = _originalFavicon || '/favicon.ico';
    // Also clear title badge
    document.title = document.title.replace(/^\(\d+\) /, '');
    return;
  }

  // Update title
  const baseTitle = document.title.replace(/^\(\d+\) /, '');
  document.title = `(${count > 99 ? '99+' : count}) ${baseTitle}`;

  // Draw favicon with badge
  const canvas = document.createElement('canvas');
  canvas.width = 32;
  canvas.height = 32;
  const ctx = canvas.getContext('2d');

  // Draw original favicon
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    ctx.drawImage(img, 0, 0, 32, 32);

    // Draw red badge circle
    const badgeX = 22, badgeY = 6, badgeR = 9;
    ctx.beginPath();
    ctx.arc(badgeX, badgeY, badgeR, 0, Math.PI * 2);
    ctx.fillStyle = '#ef4444';
    ctx.fill();

    // Draw count text
    ctx.fillStyle = 'white';
    ctx.font = 'bold ' + (count > 9 ? '8' : '10') + 'px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(count > 99 ? '99+' : String(count), badgeX, badgeY);

    link.href = canvas.toDataURL('image/png');
  };
  img.onerror = () => {
    // If favicon fails to load, draw plain red circle
    ctx.fillStyle = '#6c3ef4';
    ctx.fillRect(0, 0, 32, 32);
    ctx.beginPath();
    ctx.arc(22, 6, 9, 0, Math.PI * 2);
    ctx.fillStyle = '#ef4444';
    ctx.fill();
    ctx.fillStyle = 'white';
    ctx.font = 'bold 10px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(count > 99 ? '99+' : String(count), 22, 6);
    link.href = canvas.toDataURL('image/png');
  };
  img.src = _originalFavicon || '/favicon.ico';
}

function _updateTotalUnreadBadge() {
  const rooms = getRooms();
  const total = rooms.reduce((sum, room) => {
    return sum + (room.getUnreadNotificationCount('total') || 0);
  }, 0);
  updateFaviconBadge(total);
}

/* ──────────────────────────────────────────────
   Stories
─────────────────────────────────────────────── */
let _storiesRoomId = null;

async function _initStories() {
  // Show bar immediately so user sees it
  const bar = document.getElementById('stories-bar');
  if (bar) bar.style.display = 'block';
  try {
    const room = await getOrCreateStoriesRoom();
    if (!room) {
      console.warn('[stories] no room found/created');
      return;
    }
    _storiesRoomId = room.roomId;
    window._storiesRoomId = room.roomId;
    // Initial render — show bar even if no stories yet so user can add
    const bar = document.getElementById('stories-bar');
    if (bar) bar.style.display = 'block';
    _refreshStories();

    // Listen for story updates
    window.addEventListener(EV.STORY_UPDATED, ({ detail }) => {
      if (detail.roomId === _storiesRoomId) _refreshStories();
    });

    // Also refresh on sync to pick up new stories
    window.addEventListener(EV.ROOM_UPDATE, () => {
      if (_storiesRoomId) _refreshStories();
    });
  } catch (e) {
    console.warn('[stories] init failed:', e.message);
  }
}

function _refreshStories() {
  if (!_storiesRoomId) return;
  const room = getRoom(_storiesRoomId);
  if (!room) return;
  const stories = getActiveStories(room);
  const myId = getMyUserId();

  renderStoriesBar(stories, myId,
    // onView
    (story) => {
      const idx = stories.findIndex(s => s.userId === story.userId);
      showStoryViewer(stories, idx >= 0 ? idx : 0);
    },
    // onAdd
    (myStory) => {
      showStoryComposer(myStory || null, async ({ text, bgColor, color, deleted }) => {
        try {
          if (deleted) {
            await postStory(_storiesRoomId, { text: '', bgColor, color, expires: 0 });
            showToast('Story deleted', 'success');
          } else {
            await postStory(_storiesRoomId, { text, bgColor, color });
            showToast('📖 Story shared!', 'success');
          }
          setTimeout(_refreshStories, 500);
        } catch (err) {
          showToast('Failed to post story: ' + err.message, 'error');
        }
      });
    }
  );
}

/* ──────────────────────────────────────────────
   Working Hours / DND scheduler
─────────────────────────────────────────────── */
function _updateDndState() {
  const inHours = isInWorkingHours();
  const badge = document.getElementById('dnd-active-badge');
  const wh = getWorkingHours();
  const dndActive = wh?.enabled && !inHours;

  // Show/hide DND badge in dropdown
  if (badge) badge.style.display = dndActive ? 'inline-block' : 'none';

  // Store DND state for notification gating
  window._dndActive = dndActive;
}

function _initWorkingHoursScheduler() {
  _updateDndState();
  // Check every minute
  setInterval(_updateDndState, 60000);
  // Also update when settings change
  window.addEventListener('nx:working-hours-updated', _updateDndState);
}

async function handleLogout() {
  try {
    await stopMatrix();
    await authLogout();
    localStorage.removeItem('nx_last_room');
    // Remove Matrix event listeners
    window.removeEventListener(EV.SYNC_STATE,    handleSyncState);
    window.removeEventListener(EV.ROOM_UPDATE,   handleRoomUpdate);
    window.removeEventListener(EV.ROOM_TIMELINE, handleTimeline);
    window.removeEventListener(EV.TYPING,        handleTyping);
    window.removeEventListener(EV.READ_RECEIPT,  handleReadReceipt);
    state.phase        = 'AUTH';
    state.activeRoomId = null;
    state.syncReady    = false;
    gotoAuth();
  } catch (err) {
    console.error('[logout] Error:', err);
    // Force logout even if cleanup fails
    gotoAuth();
  }
}

